import { ContextReminder, IContextReminder } from '@app/models/ContextReminder'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IContextReminderState {
  readonly reminderText: string
  readonly reminders: List<IContextReminder>
  readonly showAll: boolean
  readonly showCompleted: boolean
}

const StateRecord = Record({
  reminderText: '',
  reminders: List<ContextReminder>(),
  showAll: false,
  showCompleted: false,
})

export class ContextReminderState extends StateRecord
  implements IContextReminderState {
  public static fromJS(initialState?: Partial<IContextReminderState>) {
    return new ContextReminderState(initialState)
  }

  public readonly reminderText: string
  public readonly reminders: List<IContextReminder>
  public readonly showAll: boolean
  public readonly showCompleted: boolean

  constructor(props?: Partial<IContextReminderState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  CreateNewReminder = 'CONTEXT_REMINDER::CREATE_NEW_REMINDER',
  CreateNewReminderSuccess = 'CONTEXT_REMINDER::CREATE_NEW_REMINDER_SUCCESS',
  DeleteReminder = 'CONTEXT_REMINDER::DELETE_REMINDER',
  DeleteReminderSuccess = 'CONTEXT_REMINDER::DELETE_REMINDER_SUCCESS',
  LoadReminders = 'CONTEXT_REMINDER::LOAD_REMINDERS',
  LoadRemindersSuccess = 'CONTEXT_REMINDER::LOAD_REMINDER_SUCCESS',
  SetReminderText = 'CONTEXT_REMINDER::SET_REMINDER_TEXT',
  SetShowCompleted = 'CONTEXT_REMINDER::SHOW_COMPLETED',
  SetShowCompletedSuccess = 'CONTEXT_REMINDER::SHOW_COMPLETED_SUCCESS',
  SetShowAll = 'CONTEXT_REMINDER::SHOW_ALL',
  ToggleReminderComplete = 'CONTEXT_REMINDER::TOGGLE_REMINDER_COMPLETE',
  ToggleReminderCompleteSuccess = 'CONTEXT_REMINDER::TOGGLE_REMINDER_COMPLETE_SUCCESS',
}

interface ICreateNewReminderAction {
  contextId: string
  type: ActionTypes.CreateNewReminder
  value: string
}

interface ICreateNewReminderSuccessAction {
  type: ActionTypes.CreateNewReminderSuccess
  value: IContextReminder
}

interface IDeleteReminderAction {
  contextId: string
  type: ActionTypes.DeleteReminder
}

interface IDeleteReminderSuccessAction {
  contextId: string
  type: ActionTypes.DeleteReminderSuccess
}

interface ILoadRemindersAction {
  contextId: string
  type: ActionTypes.LoadReminders
}

interface ILoadRemindersSuccessAction {
  items: List<IContextReminder>
  type: ActionTypes.LoadRemindersSuccess
}

interface ISetReminderTextAction {
  type: ActionTypes.SetReminderText
  value: string
}

interface ISetShowAllAction {
  type: ActionTypes.SetShowAll
  value: boolean
}

interface ISetShowCompletedAction {
  contextId: string
  type: ActionTypes.SetShowCompleted
  value: boolean
}

interface ISetShowCompletedSuccessAction {
  items: List<IContextReminder>
  type: ActionTypes.SetShowCompletedSuccess
  value: boolean
}

interface IToggleReminderCompleteAction {
  reminder: IContextReminder
  type: ActionTypes.ToggleReminderComplete
  value: boolean
}

interface IToggleReminderCompleteSuccessAction {
  reminder: IContextReminder
  type: ActionTypes.ToggleReminderCompleteSuccess
  value: boolean
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICreateNewReminderSuccessAction
  | IDeleteReminderSuccessAction
  | ILoadRemindersSuccessAction
  | ISetReminderTextAction
  | ISetShowAllAction
  | ISetShowCompletedSuccessAction
  | IToggleReminderCompleteSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------

function createNewReminder(
  contextId: string,
  value: string,
): ICreateNewReminderAction {
  return {
    contextId,
    type: ActionTypes.CreateNewReminder,
    value,
  }
}

function createNewReminderSuccess(
  value: IContextReminder,
): ICreateNewReminderSuccessAction {
  return {
    type: ActionTypes.CreateNewReminderSuccess,
    value,
  }
}

function deleteReminder(contextId: string): IDeleteReminderAction {
  return {
    contextId,
    type: ActionTypes.DeleteReminder,
  }
}

function deleteReminderSuccess(
  contextId: string,
): IDeleteReminderSuccessAction {
  return {
    contextId,
    type: ActionTypes.DeleteReminderSuccess,
  }
}

function loadReminders(contextId: string): ILoadRemindersAction {
  return {
    contextId,
    type: ActionTypes.LoadReminders,
  }
}

function loadRemindersSuccess(
  items: List<IContextReminder>,
): ILoadRemindersSuccessAction {
  return {
    items,
    type: ActionTypes.LoadRemindersSuccess,
  }
}

function setReminderText(value: string): ISetReminderTextAction {
  return {
    type: ActionTypes.SetReminderText,
    value,
  }
}

function setShowAll(value: boolean): ISetShowAllAction {
  return {
    type: ActionTypes.SetShowAll,
    value,
  }
}

function setShowCompleted(
  contextId: string,
  value: boolean,
): ISetShowCompletedAction {
  return {
    contextId,
    type: ActionTypes.SetShowCompleted,
    value,
  }
}

function setShowCompletedSuccess(
  items: List<IContextReminder>,
  value: boolean,
): ISetShowCompletedSuccessAction {
  return {
    items,
    type: ActionTypes.SetShowCompletedSuccess,
    value,
  }
}

function toggleReminderComplete(
  reminder: IContextReminder,
  value: boolean,
): IToggleReminderCompleteAction {
  return {
    reminder,
    type: ActionTypes.ToggleReminderComplete,
    value,
  }
}

function toggleReminderCompleteSuccess(
  reminder: IContextReminder,
  value: boolean,
): IToggleReminderCompleteSuccessAction {
  return {
    reminder,
    type: ActionTypes.ToggleReminderCompleteSuccess,
    value,
  }
}

export const actionCreators = {
  createNewReminder,
  deleteReminder,
  loadReminders,
  setReminderText,
  setShowAll,
  setShowCompleted,
  toggleReminderComplete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ContextReminderState> = (
  state: ContextReminderState = new ContextReminderState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.CreateNewReminderSuccess:
      return state
        .setIn(['reminders', state.reminders.size], action.value)
        .set('reminderText', '') as ContextReminderState

    case ActionTypes.DeleteReminderSuccess:
      const deleteReminderIndex = state.reminders.findIndex((r: any) => {
        return r.id === action.contextId
      })

      if (deleteReminderIndex > -1) {
        return state.deleteIn([
          'reminders',
          deleteReminderIndex,
        ]) as ContextReminderState
      }

      return state as ContextReminderState

    case ActionTypes.LoadRemindersSuccess:
      return state.set('reminders', action.items) as ContextReminderState

    case ActionTypes.SetReminderText:
      return state.set('reminderText', action.value) as ContextReminderState

    case ActionTypes.SetShowAll:
      return state.set('showAll', action.value) as ContextReminderState

    case ActionTypes.SetShowCompletedSuccess:
      if (action.value === true) {
        return state
          .set('showCompleted', action.value)
          .set(
            'reminders',
            state.get('reminders').concat(action.items),
          ) as ContextReminderState
      } else {
        return state.set('showCompleted', action.value).set(
          'reminders',
          state.reminders.filter((r: any) => {
            return r.isComplete === false
          }),
        ) as ContextReminderState
      }

    case ActionTypes.ToggleReminderCompleteSuccess:
      const completeReminderIndex = state.reminders.findIndex((r: any) => {
        return r.id === action.reminder.id
      })

      if (completeReminderIndex > -1) {
        if (!state.showCompleted && action.value === true) {
          return state.deleteIn([
            'reminders',
            completeReminderIndex,
          ]) as ContextReminderState
        }

        return state.setIn(
          ['reminders', completeReminderIndex, 'isComplete'],
          action.value,
        ) as ContextReminderState
      }

      return state as ContextReminderState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* createReminderSaga(action: ICreateNewReminderAction) {
  try {
    const baseUrl = '/api/ContextReminders/Create'

    const opts = {
      body: JSON.stringify({ sourceId: action.contextId, text: action.value }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)

    if (result.success === true) {
      yield put(
        createNewReminderSuccess(ContextReminder.fromJS(result.entity)),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    errorToast('Oops', 'Something went wrong')
  }
}

function* deleteReminderSaga(action: IDeleteReminderSuccessAction) {
  try {
    const baseUrl = `/api/ContextReminders/${action.contextId}`

    const opts = {
      method: RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)

    if (result.success === true) {
      yield put(deleteReminderSuccess(action.contextId))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    errorToast('Oops', 'Something went wrong')
  }
}

function* loadRemindersSaga(action: ILoadRemindersAction) {
  try {
    const baseUrl = `/api/ContextReminders/${action.contextId}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const items: List<IContextReminder> = List(
        result.results.map((r: any) => ContextReminder.fromJS(r)),
      )
      yield put(loadRemindersSuccess(items))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    errorToast('Oops', 'Something went wrong')
  }
}

function* showCompletedSaga(action: ISetShowCompletedAction) {
  try {
    const baseUrl = `/api/ContextReminders/${action.contextId}/Complete`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const items: List<IContextReminder> = List(
        result.results.map((r: any) => ContextReminder.fromJS(r)),
      )
      yield put(setShowCompletedSuccess(items, action.value))
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    errorToast('Oops', 'Something went wrong')
  }
}

function* toggleReminderCompleteSaga(action: IToggleReminderCompleteAction) {
  try {
    const baseUrl = `/api/ContextReminders/${action.reminder.id}`
    const currentState: ContextReminderState = yield select(
      (state: ApplicationState) => state.contextReminder,
    )

    const reminder = (action.reminder as ContextReminder).toJS()

    if (reminder) {
      reminder.isComplete = action.value

      const opts = {
        body: JSON.stringify(reminder),
        method: RequestMethod.PUT,
      }

      const result = yield makeAuthenticatedRequest(baseUrl, opts)

      if (result.success === true) {
        yield put(toggleReminderCompleteSuccess(action.reminder, action.value))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    errorToast('Oops', 'Something went wrong')
  }
}

function* watchCreateNewReminder() {
  yield takeEvery(ActionTypes.CreateNewReminder, createReminderSaga)
}

function* watchDeleteReminder() {
  yield takeEvery(ActionTypes.DeleteReminder, deleteReminderSaga)
}

function* watchLoadReminders() {
  yield takeEvery(ActionTypes.LoadReminders, loadRemindersSaga)
}

function* watchSetShowCompleted() {
  yield takeEvery(ActionTypes.SetShowCompleted, showCompletedSaga)
}

function* watchToggleReminderComplete() {
  yield takeEvery(
    ActionTypes.ToggleReminderComplete,
    toggleReminderCompleteSaga,
  )
}

export function* contextReminderRootSaga() {
  yield fork(watchCreateNewReminder)
  yield fork(watchDeleteReminder)
  yield fork(watchLoadReminders)
  yield fork(watchSetShowCompleted)
  yield fork(watchToggleReminderComplete)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ContextReminderState => state.contextReminder

export const selectReminders = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.reminders,
  )

export const selectReminderText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.reminderText,
  )

export const selectShowAll = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showAll,
  )

export const selectShowCompleted = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showCompleted,
  )
